import React, { useEffect, useState } from 'react';
import { getSharedDocument } from '../../api/document';
import { Btn } from '../../components/ui';
import { Icon } from '../../icons';

export default function SharedDocumentViewer() {
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [permissions, setPermissions] = useState({ view: true, download: true, edit: false });
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Extract share token from URL /shared-doc/:token
  const pathParts = window.location.pathname.split('/shared-doc/');
  const token = pathParts[1] ? pathParts[1].replace(/\/$/, '') : null;

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing document share link.');
      setLoading(false);
      return;
    }

    const fetchDoc = async () => {
      try {
        setLoading(true);
        const res = await getSharedDocument(token);
        setDoc(res.data?.data || res.data || null);
        setPermissions(res.data?.permissions || { view: true, download: true, edit: false });
      } catch (err) {
        if (err.response?.status === 410 || err.response?.data?.is_expired) {
          setIsExpired(true);
          setError(err.response?.data?.message || 'This document link has expired and is no longer accessible.');
        } else if (err.response?.status === 404) {
          setError('The requested shared document was not found or has been removed.');
        } else {
          setError('Unable to load the shared document. Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [token]);

  useEffect(() => {
    if (doc?.name) {
      document.title = `${doc.name} | The Clover App`;
    } else {
      document.title = 'Shared Document | The Clover App';
    }
  }, [doc]);

  const getFileTypeInfo = (d) => {
    const type = (d?.file_type || '').toLowerCase();
    const name = (d?.name || '').toLowerCase();
    const url = (d?.file_url || '').toLowerCase();

    if (type.includes('pdf') || name.endsWith('.pdf') || url.includes('.pdf')) {
      return { icon: 'file_text', color: 'text-red-500', bg: 'bg-red-500/10', label: 'PDF Document' };
    }
    if (type.includes('image') || name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/) || url.match(/\.(jpg|jpeg|png|gif|webp|svg)/)) {
      return { icon: 'file_image', color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Image' };
    }
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv') || name.match(/\.(xls|xlsx|csv)$/)) {
      return { icon: 'file_spreadsheet', color: 'text-teal-500', bg: 'bg-teal-500/10', label: 'Spreadsheet' };
    }
    return { icon: 'file', color: 'text-blue-500', bg: 'bg-blue-500/10', label: d?.file_type || 'Document' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-white">
        <div className="w-10 h-10 border-[3px] border-slate-800 border-t-primary rounded-full animate-spin" />
        <p className="mt-4 text-sm text-slate-400">Loading shared document...</p>
      </div>
    );
  }

  if (error || isExpired) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="max-w-md w-full p-8 rounded-2xl bg-slate-900/80 border border-slate-800 backdrop-blur-xl shadow-2xl space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500">
            <Icon name={isExpired ? "clock" : "shield"} size={32} />
          </div>
          <h2 className="text-xl font-bold text-white">
            {isExpired ? "Link Expired" : "Access Restricted"}
          </h2>
          <p className="text-sm text-slate-400 leading-relaxed">{error}</p>
          <div className="pt-2">
            <Btn variant="secondary" onClick={() => window.location.href = '/'}>
              Return to Homepage
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  const typeInfo = getFileTypeInfo(doc);
  const isImage = typeInfo.label === 'Image';
  const isPdf = typeInfo.label === 'PDF Document';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Top Navigation Bar */}
      <header className="h-16 border-b border-slate-800/80 bg-slate-900/60 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeInfo.bg}`}>
            <Icon name={typeInfo.icon} size={22} className={typeInfo.color} />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-base text-white truncate max-w-xs sm:max-w-md">{doc.name}</h1>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{doc.module}</span>
              <span>•</span>
              <span>v{doc.version || '1.0'}</span>
              <span>•</span>
              <span>{doc.file_size || '-'}</span>
            </div>
          </div>
        </div>

        {/* Action Controls based on Permissions */}
        <div className="flex items-center gap-2.5">
          {permissions.download && (
            <Btn
              variant="primary"
              icon="download"
              onClick={() => {
                const dl = doc.download_url || doc.file_url;
                if (dl) window.location.href = dl;
              }}
            >
              Download File
            </Btn>
          )}
          {permissions.view && (
            <Btn
              variant="secondary"
              icon="eye"
              onClick={() => {
                if (doc.file_url) window.open(doc.file_url, '_blank');
              }}
            >
              Open External
            </Btn>
          )}
        </div>
      </header>

      {/* Main Document Preview Body */}
      <main className="flex-1 flex flex-col p-4 sm:p-8 items-center justify-center max-w-6xl w-full mx-auto">
        <div className="w-full h-full min-h-[550px] rounded-2xl bg-slate-900/50 border border-slate-800/80 overflow-hidden flex flex-col items-center justify-center p-4">
          {isImage && doc.file_url ? (
            <div className="flex items-center justify-center w-full h-full max-h-[70vh] overflow-hidden">
              <img
                src={doc.file_url}
                alt={doc.name}
                className="max-h-[65vh] max-w-full object-contain rounded-xl shadow-lg"
              />
            </div>
          ) : isPdf && doc.file_url ? (
            <iframe
              src={doc.file_url}
              title={doc.name}
              className="w-full h-[75vh] rounded-xl border border-slate-800"
            />
          ) : (
            <div className="text-center space-y-4 max-w-md p-6">
              <div className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center ${typeInfo.bg}`}>
                <Icon name={typeInfo.icon} size={40} className={typeInfo.color} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-white">{doc.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{doc.description || 'Securely shared document'}</p>
              </div>
              <div className="flex justify-center gap-2 pt-2">
                {permissions.download && (
                  <Btn
                    variant="primary"
                    icon="download"
                    onClick={() => {
                      const dl = doc.download_url || doc.file_url;
                      if (dl) window.location.href = dl;
                    }}
                  >
                    Download ({doc.file_size || 'File'})
                  </Btn>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer info badge */}
        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
          <span>Protected Access Link</span>
          <span>•</span>
          <span>Shared by {doc.uploader?.name || 'Society Admin'}</span>
          {doc.expires_at && (
            <>
              <span>•</span>
              <span>Expires on {new Date(doc.expires_at).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
